import type { User } from "firebase/auth";
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import * as api from "./api";
import { go } from "./hooks";
import { SkeletonBar } from "./Skeleton";
import { type JumpItem, type JumpSection, placeItems, recentItems, search, type Visit } from "./jump";
import {
  allOpen,
  ancestors,
  fullLabel,
  groupIds,
  type Icon,
  isPage,
  mainGroup,
  menuBaseline,
  menuOrder,
  menuStep,
  type NavNode,
  type NavPage,
  openAtMount,
  pathOf,
  pinnedGroup,
  pinTarget,
  readMenuHeld,
  tabLink,
  tabsOf,
  trail,
  writeMenuHeld,
} from "./nav";

export const WIDE = "(min-width: 64rem)";

export function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Without storage, the menu forgets this choice on the next visit.
  }
}

export function useMedia(media: string): boolean {
  const [matches, setMatches] = useState(() => matchMedia(media).matches);
  useEffect(() => {
    const query = matchMedia(media);
    const change = () => setMatches(query.matches);
    change();
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, [media]);
  return matches;
}

export const useWide = (): boolean => useMedia(WIDE);

export function useHash(): string {
  const [hash, setHash] = useState(() => location.hash);
  useEffect(() => {
    const change = () => setHash(location.hash);
    addEventListener("popstate", change);
    addEventListener("hashchange", change);
    return () => {
      removeEventListener("popstate", change);
      removeEventListener("hashchange", change);
    };
  }, []);
  return hash;
}

export function navigate(path: string) {
  history.pushState(null, "", path);
  dispatchEvent(new PopStateEvent("popstate"));
}

/** The element where typing goes, so that "/" must not open the search there. */
export function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export function Svg({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

export const MenuIcon = () => (
  <Svg size={22}>
    <path d="M3 6h18M3 12h18M3 18h18" />
  </Svg>
);

export const SearchIcon = () => (
  <Svg>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);

const CloseIcon = () => (
  <Svg size={22}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
);

export const Chevron = ({ open }: { open: boolean }) => (
  <Svg size={16}>
    <path d={open ? "m6 9 6 6 6-6" : "m9 6 6 6-6 6"} />
  </Svg>
);

// Chevrons that point apart expand all groups; chevrons that point together collapse them.
const ChevronsIcon = ({ open }: { open: boolean }) => (
  <Svg size={16}>
    <path d={open ? "m7 20 5-5 5 5M7 4l5 5 5-5" : "m7 15 5 5 5-5M7 9l5-5 5 5"} />
  </Svg>
);

const PinIcon = ({ size = 20 }: { size?: number }) => (
  <Svg size={size}>
    <path d="M9 4h6l-1 6 3 3v2H7v-2l3-3zM12 15v6" />
  </Svg>
);

export const WrenchIcon = ({ size = 20 }: { size?: number }) => (
  <Svg size={size}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </Svg>
);

export const InstallIcon = ({ size = 20 }: { size?: number }) => (
  <Svg size={size}>
    <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
  </Svg>
);

const ICONS: Record<Icon, ReactNode> = {
  home: (
    <Svg>
      <path d="M3 11l9-7 9 7M5 9.5V20h5v-6h4v6h5V9.5" />
    </Svg>
  ),
  pin: <PinIcon />,
  drive: (
    <Svg>
      <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
    </Svg>
  ),
  dispatch: (
    <Svg>
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="6" r="2" />
      <path d="M8 18h7a3 3 0 0 0 0-6H9a3 3 0 0 1 0-6h7" />
    </Svg>
  ),
  monitor: (
    <Svg>
      <path d="M3 12h4l3-7 4 14 3-7h4" />
    </Svg>
  ),
  review: (
    <Svg>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="m8 12 3 3 5-6" />
    </Svg>
  ),
  process: (
    <Svg>
      <path d="M3 21V10l6 4V10l6 4V4h6v17zM7 17h2M13 17h2" />
    </Svg>
  ),
  sell: (
    <Svg>
      <path d="M3 17l6-6 4 4 8-8M15 7h6v6" />
    </Svg>
  ),
  pay: (
    <Svg>
      <path d="M12 3v18M17 7H9.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5H6" />
    </Svg>
  ),
  manage: (
    <Svg>
      <path d="M3 7l9-4 9 4-9 4zM3 12l9 4 9-4M3 17l9 4 9-4" />
    </Svg>
  ),
  analyze: (
    <Svg>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </Svg>
  ),
  settings: (
    <Svg>
      <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="18" cy="18" r="2" />
    </Svg>
  ),
  admin: <WrenchIcon />,
};

export function SideMenu({
  groups,
  roles,
  base,
  current,
  exact,
  pins,
  pinProblem,
  onPin,
  onFollow,
  uid,
  openAll,
  known = true,
}: {
  groups: NavNode[];
  roles: readonly string[];
  base: string;
  current: string | undefined;
  exact: boolean;
  pins: readonly string[];
  pinProblem: string;
  onPin: (id: string) => void;
  onFollow?: () => void;
  uid: string;
  // The "open all menu groups" setting (U-34); undefined until it is known.
  openAll?: boolean;
  // False while the roles load: the menu shows no group, and the first roles open no group.
  known?: boolean;
}) {
  const above = (id: string | undefined) => ancestors(id, groups).map((node) => node.id);
  const main = mainGroup(roles, groups);
  const path = above(current).join(" ");
  const pinned = pinnedGroup(pins, groups);
  const shownGroups = menuOrder(groups.filter((group) => !group.headerOnly));
  const ids = [...(pinned ? ["pinned"] : []), ...groupIds(shownGroups)];
  const idsKey = ids.join(" ");
  const [held] = useState(() => readMenuHeld(uid));
  const seenIds = useRef(new Set(held?.known ?? []));
  const [open, setOpen] = useState<Set<string>>(() =>
    openAtMount(held, ["pinned", ...(main ? [main] : []), ...above(current)], { main, path: path ? path.split(" ") : [] }, openAll ? ids : undefined),
  );
  const seen = useRef(menuBaseline(held, { main, path: path ? path.split(" ") : [] }));
  useLayoutEffect(() => {
    const step = menuStep(seen.current, { main, path: path ? path.split(" ") : [] }, known);
    const ids = step.open;
    seen.current = step.seen;
    if (ids.length) {
      console.info(`menu: open ${ids.join(", ")} after a change of page or role`);
      setOpen((shown) => (ids.every((id) => shown.has(id)) ? shown : new Set([...shown, ...ids])));
    }
  }, [main, path, known]);
  useLayoutEffect(() => {
    if (openAll === undefined) return;
    const all = idsKey ? idsKey.split(" ") : [];
    const unseen = openAll ? all.filter((id) => !seenIds.current.has(id)) : [];
    for (const id of all) seenIds.current.add(id);
    if (unseen.length) setOpen((shown) => new Set([...shown, ...unseen]));
  }, [openAll, idsKey]);
  useEffect(
    () => writeMenuHeld(uid, { open: [...open], main: seen.current.main, path: [...seen.current.path], known: [...seenIds.current] }),
    [uid, open, main, path, known, openAll, idsKey],
  );
  const everyOpen = allOpen(open, ids);
  const expandAll = () => {
    const next = everyOpen ? new Set<string>() : new Set([...open, ...ids]);
    setOpen(next);
    console.info(`menu: ${everyOpen ? "collapse" : "expand"} all (${ids.length} groups)`);
  };
  const toggle = (id: string) =>
    setOpen((shown) => {
      const next = new Set(shown);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const follow = (event: MouseEvent<HTMLAnchorElement>) => {
    go(event);
    if (event.defaultPrevented) onFollow?.();
  };
  const keepFocus = (button: HTMLElement, id: string) => {
    const row = button.closest("li");
    const next = (row?.nextElementSibling ?? row?.previousElementSibling)?.querySelector("a");
    const group = above(id).at(-1);
    const home = button.closest("nav")?.querySelector<HTMLElement>(`[data-testid="nav-group-${group}"]`);
    (next ?? home)?.focus();
  };
  const leaf = (page: NavPage, inPins: boolean, depth: number, row: NavNode = page) => {
    const target = inPins ? page : (pinTarget(row, current) ?? page);
    const isPinned = pins.includes(target.id);
    const top = depth === 0 && !inPins;
    const here = row === page ? page.id === current : (row.children ?? []).some((tab) => tab.id === current);
    return (
      <li key={row.id} className={top ? "menu-page menu-top" : "menu-page"}>
        <a
          href={`${base}${pathOf(page)}`}
          aria-current={here ? (exact ? "page" : "true") : undefined}
          data-testid={inPins ? `nav-pinned-${page.id}` : `nav-${row.id}`}
          data-nav={inPins ? undefined : row.id}
          onClick={follow}
        >
          {top && row.icon && ICONS[row.icon]}
          {top ? <span>{row.label}</span> : row.label}
        </a>
        <button
          type="button"
          className="pin"
          aria-pressed={isPinned}
          aria-label={`${isPinned ? "Unpin" : "Pin"} ${inPins ? page.label : fullLabel(target, groups)}`}
          title={isPinned ? "Unpin" : "Pin"}
          data-testid={inPins ? `unpin-${page.id}` : `pin-${page.id}`}
          onClick={(event) => {
            if (inPins) keepFocus(event.currentTarget, page.id);
            onPin(target.id);
          }}
        >
          <PinIcon size={16} />
        </button>
      </li>
    );
  };
  const node = (item: NavNode, inPins: boolean, depth: number): ReactNode => {
    if (isPage(item)) return item.headerOnly && !inPins ? null : leaf(item, inPins, depth);
    const firstTab = tabLink(item);
    if (firstTab) return leaf(firstTab, inPins, depth, item);
    const shown = open.has(item.id);
    return (
      <li
        key={item.id}
        className={inPins ? "menu-pinned" : depth > 0 ? "menu-subgroup" : undefined}
        data-testid={inPins ? "pinned-group" : undefined}
      >
        <button
          type="button"
          className="menu-group"
          aria-expanded={shown}
          aria-controls={`menu-${item.id}`}
          data-testid={`nav-group-${item.id}`}
          onClick={() => toggle(item.id)}
        >
          {item.icon && ICONS[item.icon]}
          <span>{item.label}</span>
          <Chevron open={shown} />
        </button>
        <ul id={`menu-${item.id}`} className="menu-pages" hidden={!shown}>
          {(item.children ?? []).map((child) => node(child, inPins, depth + 1))}
        </ul>
      </li>
    );
  };
  return (
    <nav aria-label="Main" className="menu" data-testid="main-menu" aria-busy={!known || undefined}>
      {known && ids.length > 1 && (
        <div className="menu-tools">
          <button
            type="button"
            className="menu-expand"
            data-testid="menu-expand-all"
            aria-label={everyOpen ? "Collapse all menu groups" : "Expand all menu groups"}
            title={everyOpen ? "Collapse all" : "Expand all"}
            onClick={expandAll}
          >
            <ChevronsIcon open={everyOpen} />
          </button>
        </div>
      )}
      <p className="pin-problem error" role="status">
        {pinProblem}
      </p>
      {known && (
        <ul className="menu-groups">
          {pinned && node(pinned, true, 0)}
          {shownGroups.map((group) => node(group, false, 0))}
        </ul>
      )}
    </nav>
  );
}

/** The place of the page in the tree, for example "Manage / Accounts / D-0042". */
export function Breadcrumbs({
  groups,
  base,
  current,
  exact,
  leaf,
}: {
  groups: NavNode[];
  base: string;
  current: string | undefined;
  exact: boolean;
  leaf: string | null;
}) {
  const nodes = trail(current, groups);
  if (!nodes.length) return null;
  const last = exact ? nodes.length - 1 : -1;
  return (
    <nav aria-label="Breadcrumb" className="crumbs" data-testid="breadcrumbs">
      <ol>
        {nodes.map((node, at) => (
          <li key={node.id}>
            {at === last ? (
              <span aria-current="page">{node.label}</span>
            ) : isPage(node) || node.tabs ? (
              <a href={`${base}${pathOf(isPage(node) ? node : (node.children ?? []).filter(isPage)[0])}`} onClick={go}>
                {node.label}
              </a>
            ) : (
              <span>{node.label}</span>
            )}
          </li>
        ))}
        {!exact && leaf && (
          <li>
            <span aria-current="page">{leaf}</span>
          </li>
        )}
      </ol>
    </nav>
  );
}

export const TabsContext = createContext<{
  groups: NavNode[];
  base: string;
  current: string | undefined;
  pins?: readonly string[];
  onPin?: (id: string) => void;
  // False while the menu of the role is not known: the tab bar keeps its place with grey bars.
  known?: boolean;
}>({
  groups: [],
  base: "",
  current: undefined,
});

/** The h1 of a page with tabs, and its tab bar when the role sees two tabs or more (U-19). */
export function TabHead() {
  const { groups, base, current, pins, onPin, known = true } = useContext(TabsContext);
  const parent = known ? tabsOf(current, groups) : tabsOf(current);
  const tabs = (parent?.children ?? []).filter(isPage);
  const open = tabs.find((tab) => tab.id === current);
  const pinned = open !== undefined && (pins ?? []).includes(open.id);
  return (
    <>
      <div className="tab-title">
        <h1 tabIndex={-1}>{parent?.label ?? "Containers"}</h1>
        {open && onPin && (
          <button
            type="button"
            className="pin"
            aria-pressed={pinned}
            aria-label={`${pinned ? "Unpin" : "Pin"} ${fullLabel(open, groups)}`}
            title={pinned ? "Unpin" : "Pin"}
            data-testid="tab-pin"
            onClick={() => onPin(open.id)}
          >
            <PinIcon size={18} />
          </button>
        )}
      </div>
      {!known && tabs.length > 1 && (
        <div className="page-tabs skeleton" aria-hidden="true">
          {tabs.map((tab) => (
            <span key={tab.id} className="skeleton-page-tab">
              <SkeletonBar width="100%" />
            </span>
          ))}
        </div>
      )}
      {known && tabs.length > 1 && (
        <nav aria-label={`${parent?.label} tabs`} className="page-tabs" data-testid="page-tabs">
          {tabs.map((tab) => (
            <a
              key={tab.id}
              href={`${base}${pathOf(tab)}`}
              aria-current={tab.id === current ? "page" : undefined}
              data-testid={`tab-${tab.id}`}
              onClick={go}
            >
              {tab.label}
            </a>
          ))}
        </nav>
      )}
    </>
  );
}

/** A modal dialog: the browser keeps the focus in it and makes the page under it inert. */
export function Modal({
  id,
  label,
  open,
  className,
  onDismiss,
  children,
}: {
  id: string;
  label: string;
  open: boolean;
  className: string;
  onDismiss: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = before;
    };
  }, [open]);
  return (
    <dialog
      ref={ref}
      id={id}
      aria-label={label}
      className={className}
      data-testid={id}
      onCancel={(event) => {
        event.preventDefault();
        onDismiss();
      }}
      onClose={() => {
        // A close that the browser starts must also close the React state.
        if (open) onDismiss();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      {open && <div className="modal-body">{children}</div>}
    </dialog>
  );
}

export function Drawer({
  open,
  onDismiss,
  children,
}: {
  open: boolean;
  onDismiss: () => void;
  children: ReactNode;
}) {
  return (
    <Modal id="menu-dialog" label="Menu" open={open} className="drawer" onDismiss={onDismiss}>
      <div className="drawer-head">
        <button type="button" className="icon-button" aria-label="Close the menu" data-testid="menu-close" onClick={onDismiss}>
          <CloseIcon />
        </button>
      </div>
      {children}
    </Modal>
  );
}

type Found = { uid: string; accounts?: api.Account[]; tanks?: api.Tank[] };
let cache: Found | null = null;

export function Jump({
  open,
  user,
  base,
  groups,
  views,
  visits,
  pins,
  onFollow,
  onDismiss,
}: {
  open: boolean;
  user: User;
  base: string;
  groups: NavNode[];
  views: readonly string[] | undefined;
  visits: Visit[];
  pins: readonly string[];
  onFollow: () => void;
  onDismiss: () => void;
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [found, setFound] = useState<Found | null>(() => (cache?.uid === user.uid ? cache : null));
  const [failed, setFailed] = useState(false);
  const wantsAccounts = Boolean(views?.includes("account"));

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    if (cache?.uid === user.uid && (cache.accounts || !wantsAccounts)) {
      setFound(cache);
      return;
    }
    let live = true;
    setFailed(false);
    void (async () => {
      try {
        const token = await user.getIdToken();
        const [tanks, accounts] = await Promise.all([
          api.tanks(token),
          wantsAccounts ? api.accounts(token) : Promise.resolve(undefined),
        ]);
        cache = { uid: user.uid, tanks, accounts };
        if (live) setFound(cache);
      } catch {
        if (live) setFailed(true);
      }
    })();
    return () => {
      live = false;
    };
  }, [open, user, wantsAccounts]);

  const text = query.trim();
  const sections: JumpSection[] = text
    ? search(text, groups, base, found?.accounts ?? [], found?.tanks ?? [])
    : [
        {
          id: "pinned" as const,
          title: "Pinned",
          items: placeItems([pinnedGroup(pins, groups)].filter((g): g is NavNode => g !== undefined), base).map((item) => ({
            ...item,
            key: `pinned:${item.key}`,
          })),
        },
        { id: "places" as const, title: "Places", items: placeItems(menuOrder(groups), base) },
        {
          id: "recent" as const,
          title: "Recently visited",
          items: recentItems(visits, groups, base, found?.accounts, found?.tanks),
        },
      ].filter((section) => section.items.length > 0);
  const items = sections.flatMap((section) => section.items);
  const at = Math.min(active, Math.max(items.length - 1, 0));

  const choose = (item: JumpItem | undefined) => {
    if (!item) return;
    onFollow();
    navigate(item.path);
  };
  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!items.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((at + step + items.length) % items.length);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      setActive(event.key === "Home" ? 0 : items.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(items[at]);
    }
  };
  useEffect(() => {
    document.getElementById(`jump-option-${at}`)?.scrollIntoView({ block: "nearest" });
  }, [at]);

  const loading = !found && !failed;
  let index = -1;
  return (
    <Modal id="jump-dialog" label="Search or go to" open={open} className="jump" onDismiss={onDismiss}>
      <div className="jump-field">
        <SearchIcon />
        <input
          type="text"
          role="combobox"
          aria-label="Search or go to"
          aria-expanded="true"
          aria-controls="jump-list"
          aria-autocomplete="list"
          aria-activedescendant={items.length ? `jump-option-${at}` : undefined}
          placeholder="Search or go to…"
          autoComplete="off"
          spellCheck={false}
          data-testid="jump-input"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={keys}
        />
        <button type="button" className="icon-button" aria-label="Close the search" onClick={onDismiss}>
          <CloseIcon />
        </button>
      </div>
      <div id="jump-list" role="listbox" aria-label="Places and records" className="jump-list">
        {sections.map((section) => (
          <div key={section.id} role="group" aria-labelledby={`jump-title-${section.id}`}>
            <div id={`jump-title-${section.id}`} role="presentation" className="jump-title">
              {section.title}
            </div>
            {section.items.map((item) => {
              index += 1;
              const mine = index;
              return (
                <div
                  key={item.key}
                  id={`jump-option-${mine}`}
                  role="option"
                  aria-selected={mine === at}
                  className="jump-option"
                  data-testid="jump-option"
                  onMouseMove={() => setActive(mine)}
                  onClick={() => choose(item)}
                >
                  <span>{item.label}</span>
                  {item.detail && <span className="jump-detail">{item.detail}</span>}
                </div>
              );
            })}
            {section.more ? (
              <div role="presentation" className="jump-more" data-testid="jump-more">
                {section.more} more. Type more of the name to find them.
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <p className="jump-status" role="status">
        {failed
          ? "Cannot load the accounts and containers. The places still work."
          : loading && text
            ? "Loading the accounts and containers…"
            : text && !items.length
              ? `Nothing matches “${text}”.`
              : ""}
      </p>
    </Modal>
  );
}

/** The search box of the header. It opens the same dialog as "/"; a narrow screen shows only the icon. */
export function HeaderSearch({ onOpen }: { onOpen: (from: HTMLElement) => void }) {
  return (
    <button
      type="button"
      className="header-search"
      aria-label="Search or go to"
      aria-keyshortcuts="/ Control+K"
      data-testid="jump-open-header"
      onClick={(event) => onOpen(event.currentTarget)}
    >
      <SearchIcon />
      <span className="header-search-text">Search or go to…</span>
      <kbd aria-hidden="true">/</kbd>
    </button>
  );
}

/** Holds the place of the Admin link until the role is known, so that the search box stays. */
export function AdminSlot() {
  return (
    <span className="settings-link admin-link admin-slot" aria-hidden="true">
      <WrenchIcon size={22} />
      <span className="admin-label">Admin</span>
    </span>
  );
}

/** A header list that opens under its button; a click outside or Escape closes it. */
export function Dropdown({
  className,
  testId,
  label,
  summary,
  children,
}: {
  className: string;
  testId: string;
  label: string;
  summary: ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const menu = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const outside = (event: globalThis.MouseEvent) => {
      if (menu.current && !menu.current.contains(event.target as Node)) menu.current.open = false;
    };
    document.addEventListener("click", outside);
    return () => document.removeEventListener("click", outside);
  }, [open]);
  const close = () => {
    if (menu.current) menu.current.open = false;
  };
  return (
    <details
      ref={menu}
      className={`dropdown ${className}`}
      data-testid={testId}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      onKeyDown={(event) => {
        if (event.key === "Escape" && menu.current?.open) {
          close();
          menu.current.querySelector("summary")?.focus();
        }
      }}
    >
      <summary aria-label={label} title={label}>
        {summary}
      </summary>
      <div className="dropdown-panel">{children(close)}</div>
    </details>
  );
}

/** Focus this element after the dialog closes and the page under it is no longer inert. */
export function refocus(target: RefObject<HTMLElement | null> | HTMLElement | null) {
  requestAnimationFrame(() => {
    const element = target && "current" in target ? target.current : target;
    element?.focus();
  });
}
